import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { StateContext } from "types/intrinsicElements.types";
import { OnOptionSelect, SelectOption } from "../../../../types/select.types";
import { toggleMultipleSelection } from "../../../../utils/select.utils";

const SelectionCtx = React.createContext<StateContext<SelectOption[], OnOptionSelect>>(null);

export const useSelection = () => {
  const ctx = useContext(SelectionCtx);
  if (!ctx) throw new Error("useSelection must be used inside SelectProvider");
  return ctx;
};

const OpenCtx = React.createContext<StateContext<boolean>>(null);

export const useOpenState = () => {
  const ctx = useContext(OpenCtx);
  if (!ctx) throw new Error("useOpenState must be used inside SelectProvider");
  return ctx;
};

type SelectConfig = {
  closeAfterSelection: boolean;
  withCheckbox: boolean;
} | null;

const ConfigCtx = React.createContext<SelectConfig>(null);

export const useSelectConfig = () => {
  const ctx = useContext(ConfigCtx);
  if (!ctx) throw new Error("useSelectConfig must be used inside SelectProvider");
  return ctx;
};

interface SelectProviderProps {
  closeAfterSelection: boolean;
  withCheckbox: boolean;
  initialValue?: SelectOption[];
  multiple?: boolean;
  onSelectionChange?: OnOptionSelect;
  children?: ReactNode;
}

const normalizeSelectOptions = (value?: SelectOption[]): SelectOption[] =>
  (value ?? []).filter((item): item is SelectOption => Boolean(item));

const getSelectOptionsKey = (value?: SelectOption[]) =>
  JSON.stringify(normalizeSelectOptions(value).map(({ name, value: optionValue }) => `${name}:${optionValue}`));

export const SelectProvider = ({
  children,
  onSelectionChange,
  multiple,
  initialValue,
  closeAfterSelection,
  withCheckbox,
}: SelectProviderProps) => {
  const [selection, setSelection] = useState<SelectOption[]>(() => normalizeSelectOptions(initialValue));
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setSelection(normalizeSelectOptions(initialValue));
  }, [getSelectOptionsKey(initialValue)]);

  const updateSelection = useCallback(
    (option: SelectOption): void => {
      if (multiple) {
        setSelection((prev) => {
          const next = toggleMultipleSelection(option)(prev);
          onSelectionChange?.({
            value: JSON.stringify(next),
            name: JSON.stringify(next),
          });
          return next;
        });
        return;
      }

      onSelectionChange?.(option);
      setSelection([option]);
    },
    [multiple, onSelectionChange]
  );

  const selectionCtxValue: StateContext<SelectOption[], OnOptionSelect> = useMemo(
    () => [selection, updateSelection],
    [selection, updateSelection]
  );

  const openCtxValue: StateContext<boolean> = useMemo(() => [isOpen, setIsOpen], [isOpen]);

  return (
    <SelectionCtx.Provider value={selectionCtxValue}>
      <OpenCtx.Provider value={openCtxValue}>
        <ConfigCtx.Provider value={{ closeAfterSelection, withCheckbox }}>{children}</ConfigCtx.Provider>
      </OpenCtx.Provider>
    </SelectionCtx.Provider>
  );
};
